import { useEffect, useState } from 'react';
import WatchlistDashboardService, { ActivityEntryRow, StageMovementRow } from '../WatchlistDashboardService';

type TabKey = 'entry' | 'exit' | 'stage';

const LAYER_BADGE: Record<string, string> = {
    signal: 'bg-primary bg-opacity-10 text-primary-emphasis',
    strategic: 'bg-bs-purple bg-opacity-10 text-primary-emphasis',
    activation: 'bg-success bg-opacity-10 text-success-emphasis',
};

// Outcome key → full label mapping for Activation Watchlists
// Source of truth: batch-jobs/jobs/watchlist/watchlistRules.js line 12
// outcome ∈ {hiring, mentorship, donor, am, at, ra}
const OUTCOME_LABELS: Record<string, string> = {
    hiring: 'Hiring',
    mentorship: 'Career Guidance',
    donor: 'Donor', 
    at: 'Academics & Thought Leadership',
    am: 'Admissions & Marketing',
    ra: 'Ranking & Accreditation',
};

const fmtDate = (iso: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * Stage 4 — Activity Logs (3 tabs: Entry / Exit / Stage Movement). Reads the nightly event tables.
 * On Day 1 these are empty (diff skipped) — the tables render an empty-state row, which is correct.
 */
const PAGE_SIZE = 10;

const ActivityLogsSection = ({ instituteId, filterInstituteId }: { instituteId: string | number; filterInstituteId?: string | null }) => {
    const [tab, setTab] = useState<TabKey>('entry');
    const [entries, setEntries] = useState<ActivityEntryRow[]>([]);
    const [exits, setExits] = useState<ActivityEntryRow[]>([]);
    const [moves, setMoves] = useState<StageMovementRow[]>([]);
    const [page, setPage] = useState(0); // 0-based, applies to the active tab
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Filters. Layer applies to Entry/Exit only (Stage Movement is Layer 3 only). Empty date = backend
    // default window (last 30 days); set a from_date to page back into older logs.
    const [layer, setLayer] = useState<string>('all');
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    // Reset to the first page when the institute / campus filter or any log filter changes.
    useEffect(() => { setPage(0); }, [instituteId, filterInstituteId, layer, fromDate, toDate]);

    useEffect(() => {
        setLoading(true);
        setError(null);
        const opts = { limit: PAGE_SIZE, offset: page * PAGE_SIZE, filterInstituteId, fromDate: fromDate || null, toDate: toDate || null };
        const fetcher =
            tab === 'entry'
                ? WatchlistDashboardService.getActivityEntries(instituteId, { ...opts, layer }).then((r) => { setEntries(r.entries); setTotal(r.total); })
                : tab === 'exit'
                ? WatchlistDashboardService.getActivityExits(instituteId, { ...opts, layer }).then((r) => { setExits(r.entries); setTotal(r.total); })
                : WatchlistDashboardService.getActivityStageMovements(instituteId, opts).then((r) => { setMoves(r.entries); setTotal(r.total); });
        fetcher.catch((err) => setError(err?.message || 'Failed to load activity logs.')).finally(() => setLoading(false));
    }, [tab, instituteId, filterInstituteId, page, layer, fromDate, toDate]);

    // Switching tabs resets to the first page so the pager and data stay in sync.
    const selectTab = (id: TabKey) => { setTab(id); setPage(0); };

    const TabBtn = ({ id, label }: { id: TabKey; label: string }) => (
        <div
            onClick={() => selectTab(id)}
            className={`px-4 py-2 text-center cursor-pointer font-13 font-600 border-bottom border-2 ${
                tab === id ? 'text-dark-blue border-dark' : 'text-secondary-emphasis border-white'
            }`}>
            {label}
        </div>
    );

    return (
        <div className="col-12">
            <div className="d-flex align-items-center gap-3 mt-4">
                <h3 className="font-18 font-600 text-dark-blue m-0">Activity Logs</h3>
                <p className="m-0 font-12 text-secondary-emphasis">Recent entries, exits and stage advancements across all watchlists</p>
            </div>

            <div className="d-flex flex-wrap border-bottom border-secondary border-opacity-25 mt-2">
                <TabBtn id="entry" label="📥 Entry Log" />
                <TabBtn id="exit" label="📤 Exit Log" />
                <TabBtn id="stage" label="⬆️ Stage Movement Log" />
            </div>

            <div className="d-flex flex-wrap align-items-end gap-3 bg-white border border-bottom-0 rounded-top px-3 py-3 mt-2">
                {tab !== 'stage' && (
                    <div>
                        <label className="d-block font-11 font-600 text-uppercase text-muted mb-1">Layer</label>
                        <select className="form-select form-select-sm" style={{ minWidth: 150 }} value={layer} onChange={(e) => setLayer(e.target.value)}>
                            <option value="all">All layers</option>
                            <option value="signal">Signal</option>
                            <option value="strategic">Strategic</option>
                            <option value="activation">Activation</option>
                        </select>
                    </div>
                )}
                <div>
                    <label className="d-block font-11 font-600 text-uppercase text-muted mb-1">From</label>
                    <input type="date" className="form-control form-control-sm" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} />
                </div>
                <div>
                    <label className="d-block font-11 font-600 text-uppercase text-muted mb-1">To</label>
                    <input type="date" className="form-control form-control-sm" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} />
                </div>
                {(layer !== 'all' || fromDate || toDate) && (
                    <button
                        className="btn btn-sm btn-light border rounded-3 font-12"
                        onClick={() => { setLayer('all'); setFromDate(''); setToDate(''); }}>
                        Clear filters
                    </button>
                )}
                <div className="ms-auto font-11 text-secondary-emphasis align-self-center">
                    {!fromDate ? 'Showing the last 30 days — set a From date to view older logs.' : ''}
                </div>
            </div>

            <div className="bg-white border overflow-hidden rounded-bottom p-0 shadow-sm mb-4">
                {loading && <div className="font-13 text-secondary-emphasis p-3">Loading…</div>}
                {error && !loading && <div className="alert alert-danger font-13 m-3">{error}</div>}

                {!loading && !error && tab === 'entry' && (
                    <LogTable
                        headers={['Alumni Name', 'Program / Batch', 'Layer', 'Watchlist', 'Stage', 'Date']}
                        rows={entries}
                        empty="No watchlist entries in the selected window."
                        render={(e: ActivityEntryRow) => (
                            <tr key={`${e.student_id}-${e.watchlist_code}-${e.event_date}`}>
                                <td className="px-4 py-2"><div className="font-600 text-dark-blue">{e.name ?? '—'}</div></td>
                                <td className="px-4 py-2"><div className="font-13 text-muted">{e.program_and_year}</div></td>
                                <td className="px-4 py-2"><span className={`${LAYER_BADGE[e.layer] || ''} font-12 px-3 py-1 rounded-pill text-nowrap text-capitalize`}>{e.layer}</span></td>
                                <td className="px-4 py-2">{e.watchlist_label}</td>
                                <td className="px-4 py-2">{e.stage ? <span className="font-11 font-600 px-2 py-1 rounded-pill bg-success bg-opacity-10 text-success border border-success border-opacity-25 text-capitalize">{e.stage}</span> : <span className="text-muted">—</span>}</td>
                                <td className="px-4 py-2">{fmtDate(e.event_date)}</td>
                            </tr>
                        )}
                    />
                )}

                {!loading && !error && tab === 'exit' && (
                    <LogTable
                        headers={['Alumni Name', 'Program / Batch', 'Watchlist Exited', 'Exit Reason', 'Exit Date']}
                        rows={exits}
                        empty="No watchlist exits in the selected window."
                        render={(e: ActivityEntryRow) => (
                            <tr key={`${e.student_id}-${e.watchlist_code}-${e.event_date}`}>
                                <td className="px-4 py-2"><div className="font-600 text-dark-blue">{e.name ?? '—'}</div></td>
                                <td className="px-4 py-2"><div className="font-13 text-muted">{e.program_and_year}</div></td>
                                <td className="px-4 py-2">{e.watchlist_label}</td>
                                <td className="px-4 py-2">{e.exit_reason_label || '—'}</td>
                                <td className="px-4 py-2">{fmtDate(e.event_date)}</td>
                            </tr>
                        )}
                    />
                )}

                {!loading && !error && tab === 'stage' && (
                    <LogTable
                        headers={['Alumni Name', 'Program / Batch', 'Activation Watchlist', 'Stage Movement', 'Direction', 'Date']}
                        rows={moves}
                        empty="No stage movements in the selected window."
                        render={(m: StageMovementRow) => (
                            <tr key={`${m.student_id}-${m.outcome}-${m.transition_date}`}>
                                <td className="px-4 py-2"><div className="font-600 text-dark-blue">{m.name ?? '—'}</div></td>
                                <td className="px-4 py-2"><div className="text-muted">{m.program_and_year}</div></td>
                                <td className="px-4 py-2">{OUTCOME_LABELS[m.outcome] || m.outcome}</td>
                                <td className="px-4 py-2">
                                    <div className="d-flex align-items-center gap-2 font-12 text-muted">
                                        <span>{m.from_stage_label || '—'}</span><span>→</span>
                                        <span className="font-600 text-success">{m.to_stage_label}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-2 text-capitalize">{m.direction}</td>
                                <td className="px-4 py-2">{fmtDate(m.transition_date)}</td>
                            </tr>
                        )}
                    />
                )}

                {!loading && !error && total > 0 && (
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2 border-top bg-light">
                        <span className="font-12 text-secondary-emphasis">
                            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
                        </span>
                        <div className="d-flex gap-2">
                            <button
                                className="btn btn-sm btn-light border rounded-3 font-12"
                                disabled={page === 0}
                                onClick={() => setPage((p) => Math.max(0, p - 1))}>
                                ← Prev
                            </button>
                            <button
                                className="btn btn-sm btn-light border rounded-3 font-12"
                                disabled={(page + 1) * PAGE_SIZE >= total}
                                onClick={() => setPage((p) => p + 1)}>
                                Next →
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const LogTable = ({ headers, rows, render, empty }: { headers: string[]; rows: any[]; render: (r: any) => JSX.Element; empty: string }) => (
    <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
            <thead className="table-light text-muted font-11 text-uppercase">
                <tr>{headers.map((h) => <th key={h} className="px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="font-13">
                {rows.length === 0 ? (
                    <tr><td className="px-4 py-3 text-muted" colSpan={headers.length}>{empty}</td></tr>
                ) : (
                    rows.map(render)
                )}
            </tbody>
        </table>
    </div>
);

export default ActivityLogsSection;
